import React, { createContext, useContext, useState, useCallback } from "react";

const META = {
  fr: {
    title: "Sily Agency — Donner de la visibilité à vos rêves",
    description: "Sily Agency, agence culturelle et marketing digital à Saint-Étienne : booking d'artistes, événements, tournées, publicité en ligne et boutique afro-contemporaine.",
  },
  en: {
    title: "Sily Agency — Giving visibility to your dreams",
    description: "Sily Agency, a cultural and digital marketing agency in Saint-Étienne, France: artist booking, events, tours, online advertising and an Afro-contemporary shop.",
  },
};

const PAGE_TITLES = {
  "/services": { fr: "Services", en: "Services" },
  "/artistes": { fr: "Nos artistes", en: "Our artists" },
  "/evenements": { fr: "Événements & Sily Tour", en: "Events & Sily Tour" },
  "/booking": { fr: "Booking d'artistes", en: "Artist booking" },
  "/boutique": { fr: "Boutique", en: "Shop" },
  "/a-propos": { fr: "À propos", en: "About" },
  "/contact": { fr: "Contact", en: "Contact" },
};

export function setHead(lang, pathname = window.location.pathname) {
  const m = META[lang];
  const key = Object.keys(PAGE_TITLES).find((k) => pathname === k || pathname.startsWith(`${k}/`));
  document.documentElement.lang = lang;
  document.title = key ? `${PAGE_TITLES[key][lang]} — Sily Agency` : m.title;
  const set = (sel, make, attrs) => {
    let el = document.head.querySelector(sel);
    if (!el) { el = document.createElement(make); document.head.appendChild(el); }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  };
  const base = window.location.origin + pathname;
  set('meta[name="description"]', "meta", { name: "description", content: m.description });
  set('meta[property="og:title"]', "meta", { property: "og:title", content: m.title });
  set('meta[property="og:description"]', "meta", { property: "og:description", content: m.description });
  set('meta[property="og:locale"]', "meta", { property: "og:locale", content: lang === "fr" ? "fr_FR" : "en_GB" });
  set('link[hreflang="fr"]', "link", { rel: "alternate", hreflang: "fr", href: base });
  set('link[hreflang="en"]', "link", { rel: "alternate", hreflang: "en", href: `${base}?lang=en` });
  set('link[hreflang="x-default"]', "link", { rel: "alternate", hreflang: "x-default", href: base });
}

function initialLang() {
  const q = new URLSearchParams(window.location.search).get("lang");
  if (q === "fr" || q === "en") { localStorage.setItem("sily_lang", q); return q; }
  const s = localStorage.getItem("sily_lang");
  return s === "en" ? "en" : "fr";
}

export function loc(obj, field, lang) {
  if (!obj) return "";
  return (lang === "en" && obj[`${field}_en`]) || obj[field] || "";
}

export const STR = {
  nav: {
    home: { fr: "Accueil", en: "Home" },
    services: { fr: "Services", en: "Services" },
    artists: { fr: "Artistes", en: "Artists" },
    events: { fr: "Événements", en: "Events" },
    booking: { fr: "Booking", en: "Booking" },
    shop: { fr: "Boutique", en: "Shop" },
    about: { fr: "À propos", en: "About" },
    contact: { fr: "Contact", en: "Contact" },
  },
  common: {
    slogan: { fr: "Donner de la visibilité à vos rêves.", en: "Giving visibility to your dreams." },
    requestBooking: { fr: "Demander un booking", en: "Request a booking" },
    developProject: { fr: "Développer mon projet", en: "Develop my project" },
    bookThisArtist: { fr: "Booker cet artiste", en: "Book this artist" },
    discoverArtists: { fr: "Découvrir les artistes", en: "Discover the artists" },
    ourServices: { fr: "Nos services", en: "Our services" },
    viewAll: { fr: "Voir tout", en: "View all" },
    learnMore: { fr: "En savoir plus", en: "Learn more" },
    send: { fr: "Envoyer", en: "Send" },
    addToCart: { fr: "Ajouter au panier", en: "Add to cart" },
    cart: { fr: "Panier", en: "Cart" },
    checkout: { fr: "Passer commande", en: "Checkout" },
    total: { fr: "Total", en: "Total" },
    emptyCart: { fr: "Votre panier est vide", en: "Your cart is empty" },
    loading: { fr: "Chargement…", en: "Loading…" },
    tickets: { fr: "Billetterie", en: "Tickets" },
    upcoming: { fr: "À venir", en: "Upcoming" },
    past: { fr: "Passés", en: "Past" },
    all: { fr: "Tous", en: "All" },
    backToArtists: { fr: "Retour aux artistes", en: "Back to artists" },
    genre: { fr: "Style musical", en: "Music style" },
    biography: { fr: "Biographie", en: "Biography" },
    videos: { fr: "Vidéos", en: "Videos" },
    socials: { fr: "Réseaux sociaux", en: "Social media" },
    tourDates: { fr: "Dates de tournée", en: "Tour dates" },
    noVideos: { fr: "Vidéos à venir prochainement.", en: "Videos coming soon." },
    noDates: { fr: "Disponible pour booking — nouvelles dates à venir.", en: "Available for booking — new dates coming soon." },
  },
  hero: {
    eyebrow: { fr: "Agence culturelle & marketing digital", en: "Cultural agency & digital marketing" },
    title: { fr: "L'art de rendre vos rêves visibles", en: "The art of making your dreams visible" },
    subtitle: {
      fr: "Sily Agency accompagne artistes et organisateurs à travers le marketing digital, le booking, l'événementiel et la production de tournées.",
      en: "Sily Agency supports artists and organizers through digital marketing, booking, events and tour production.",
    },
  },
  home: {
    aboutEyebrow: { fr: "Qui sommes-nous", en: "Who we are" },
    aboutTitle: { fr: "Une agence afro-contemporaine, élégante et internationale", en: "An Afro-contemporary, elegant and international agency" },
    aboutText: {
      fr: "Basée en France, Sily Agency conjugue création culturelle et stratégie digitale pour donner aux artistes la visibilité qu'ils méritent. Notre emblème, la tête d'éléphant, incarne la force, la sagesse et la majesté.",
      en: "Based in France, Sily Agency blends cultural creation and digital strategy to give artists the visibility they deserve. Our emblem, the elephant head, embodies strength, wisdom and majesty.",
    },
    artistsTitle: { fr: "Nos artistes", en: "Our artists" },
    eventsTitle: { fr: "Événements & Sily Tour", en: "Events & Sily Tour" },
    shopTitle: { fr: "Sily Shop", en: "Sily Shop" },
    shopText: { fr: "Artisanat, mode et produits culturels afro-contemporains.", en: "Afro-contemporary craft, fashion and cultural goods." },
    ctaTitle: { fr: "Prêt à donner de la visibilité à votre projet ?", en: "Ready to give your project visibility?" },
  },
  services: {
    eyebrow: { fr: "Ce que nous faisons", en: "What we do" },
    title: { fr: "Cinq expertises, une vision", en: "Five expertises, one vision" },
    items: [
      { key: "digital", fr: "Marketing digital & réseaux sociaux", en: "Digital marketing & social media",
        dfr: "Stratégie de contenu, gestion des réseaux et croissance de communauté.", den: "Content strategy, social management and community growth." },
      { key: "ads", fr: "Publicité en ligne / Meta Ads", en: "Online advertising / Meta Ads",
        dfr: "Campagnes publicitaires performantes sur Meta, Google et plus.", den: "High-performing ad campaigns on Meta, Google and beyond." },
      { key: "booking", fr: "Booking & représentation artistique", en: "Booking & artist representation",
        dfr: "Représentation, négociation et mise en relation avec les organisateurs.", den: "Representation, negotiation and connection with organizers." },
      { key: "events", fr: "Organisation d'événements", en: "Event organization",
        dfr: "Conception et production d'événements culturels sur mesure.", den: "Design and production of tailor-made cultural events." },
      { key: "tours", fr: "Production & organisation de tournées", en: "Tour production & organization",
        dfr: "Logistique complète et déploiement des tournées Sily Tour.", den: "Full logistics and rollout of Sily Tour tours." },
    ],
  },
  booking: {
    eyebrow: { fr: "Booking", en: "Booking" },
    title: { fr: "Réservez un artiste Sily", en: "Book a Sily artist" },
    subtitle: { fr: "Remplissez le formulaire, notre équipe vous répond sous 48h.", en: "Fill in the form, our team replies within 48h." },
    fields: {
      name: { fr: "Nom complet", en: "Full name" },
      organization: { fr: "Organisation", en: "Organization" },
      email: { fr: "Email", en: "Email" },
      phone: { fr: "Téléphone", en: "Phone" },
      eventType: { fr: "Type d'événement", en: "Event type" },
      city: { fr: "Ville", en: "City" },
      date: { fr: "Date", en: "Date" },
      artist: { fr: "Artiste souhaité", en: "Desired artist" },
      budget: { fr: "Budget", en: "Budget" },
      message: { fr: "Message", en: "Message" },
    },
    success: { fr: "Demande envoyée ! Nous vous recontactons rapidement.", en: "Request sent! We'll get back to you soon." },
  },
  contact: {
    eyebrow: { fr: "Contact", en: "Contact" },
    title: { fr: "Parlons de votre projet", en: "Let's talk about your project" },
    subject: { fr: "Sujet", en: "Subject" },
    success: { fr: "Message envoyé, merci !", en: "Message sent, thank you!" },
    whatsapp: { fr: "Discuter sur WhatsApp", en: "Chat on WhatsApp" },
  },
  about: {
    eyebrow: { fr: "À propos", en: "About" },
    title: { fr: "Notre vision", en: "Our vision" },
    visionText: {
      fr: "Sily Agency est née de la conviction que la culture afro-contemporaine mérite une scène mondiale. Nous construisons des ponts entre les talents et leur public, avec exigence et élégance.",
      en: "Sily Agency was born from the belief that Afro-contemporary culture deserves a world stage. We build bridges between talents and their audiences, with rigor and elegance.",
    },
    values: [
      { fr: "Excellence", en: "Excellence", dfr: "Un niveau d'exigence élevé sur chaque projet.", den: "A high standard on every project." },
      { fr: "Authenticité", en: "Authenticity", dfr: "Une esthétique fidèle à nos racines contemporaines.", den: "An aesthetic true to our contemporary roots." },
      { fr: "Vision internationale", en: "International vision", dfr: "Faire rayonner les artistes au-delà des frontières.", den: "Shining a light on artists beyond borders." },
    ],
  },
  admin: {
    login: { fr: "Connexion admin", en: "Admin login" },
    signInGoogle: { fr: "Se connecter avec Google", en: "Sign in with Google" },
    dashboard: { fr: "Tableau de bord", en: "Dashboard" },
    logout: { fr: "Déconnexion", en: "Logout" },
  },
};

export function get(node, lang) {
  if (!node) return "";
  return node[lang] ?? node.fr ?? "";
}

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(initialLang);
  const change = useCallback((l) => {
    setLang(l);
    localStorage.setItem("sily_lang", l);
  }, []);
  const t = useCallback((node) => get(node, lang), [lang]);
  return <LangContext.Provider value={{ lang, setLang: change, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}
